import { createContext, useContext, useState, useCallback } from "react";

const DICT = {
  es: {},
  en: {
    // Header / global
    "Reserva online": "Online booking",
    "Reserva tu traslado en Madrid": "Book your transfer in Madrid",
    "Volver": "Back",
    "Volver al inicio": "Back to home",
    "Acceso interno": "Staff login",
    "Consulta de reserva": "Booking lookup",

    // Steps
    "Trayecto": "Route",
    "Datos": "Details",
    "Resumen": "Summary",
    "Pago": "Payment",

    // Step 1 — locations, date, vehicle
    "Reserva tu trayecto": "Book your trip",
    "Lugar de recogida": "Pickup location",
    "Lugar de entrega": "Drop-off location",
    "Ej. Aeropuerto Madrid Barajas T4": "e.g. Madrid Barajas Airport T4",
    "Ej. Hotel Riu Plaza España": "e.g. Hotel Riu Plaza España",
    "Fecha (mín. 24h)": "Date (min. 24h)",
    "Hora": "Time",
    "Número de vuelo (opcional)": "Flight number (optional)",
    "Tipo de vehículo": "Vehicle type",
    "Sedán ejecutivo": "Executive sedan",
    "Furgoneta 7 plazas": "7-seater van",
    "Autobús pequeño": "Small bus",
    "pax": "pax",
    "maletas": "luggage",
    "Número de personas": "Number of passengers",
    "Equipaje": "Luggage",
    "Notas (opcional — ej. silla de bebé, máx. 1 por vehículo)": "Notes (optional — e.g. baby seat, max. 1 per vehicle)",
    "Comentarios adicionales…": "Additional comments…",
    "Continuar": "Continue",

    // Price box
    "Precio total": "Total price",
    "Cobro por km": "Per-km rate",
    "Precio base": "Base price",
    "Rellena origen y destino para ver el precio.": "Fill in pickup and drop-off to see the price.",
    "Calculando precio…": "Calculating price…",
    "No se pudo calcular el precio": "Could not calculate the price",

    // Errors
    "Rellena lugar de recogida y destino": "Fill in pickup and drop-off",
    "Fecha y hora son obligatorias": "Date and time are required",
    "La recogida debe ser al menos 24 h después de ahora": "Pickup must be at least 24h from now",
    "Mínimo 1 pasajero": "At least 1 passenger",
    "No se pudo calcular el precio. Revisa las direcciones.": "Could not calculate the price. Please review the addresses.",
    "Rellena tu nombre, email y teléfono": "Please fill in your name, email and phone",
    "Debes aceptar los términos y condiciones": "You must accept the terms and conditions",
    "El {vehicle} admite máximo {n} pasajero(s). Elige otro vehículo.":
      "The {vehicle} allows a maximum of {n} passenger(s). Please choose another vehicle.",
    "El {vehicle} admite máximo {n} maleta(s). Elige otro vehículo.":
      "The {vehicle} allows a maximum of {n} piece(s) of luggage. Please choose another vehicle.",
    "Standard": "Standard",
    "Mini Van": "Mini Van",
    "Mini Bus": "Mini Bus",

    // Step 2 — client info
    "Tus datos": "Your details",
    "Nombre completo": "Full name",
    "Teléfono móvil": "Mobile phone",
    "Correo electrónico": "Email address",

    // Step 3 — summary + T&C
    "Resumen de tu reserva": "Booking summary",
    "Revisa los detalles antes de confirmar. Todos los impuestos están incluidos.": "Review the details before confirming. All taxes are included.",
    "Detalles del servicio": "Service details",
    "Concepto": "Item",
    "Traslado privado con conductor": "Private transfer with driver",
    "Fecha": "Date",
    "Vuelo": "Flight",
    "Pasajeros": "Passengers",
    "pax · 1 maleta(s)": "pax · 1 piece of luggage",
    "maleta(s)": "piece(s) of luggage",
    "Total de la reserva": "Booking total",
    "Impuestos incluidos": "Taxes included",
    "Confirma los puntos exactos": "Confirm the exact points",
    "He leído y acepto los": "I have read and accept the",
    "términos y condiciones": "terms and conditions",
    "del servicio.": ".",
    "Confirmar reserva": "Confirm booking",
    "Términos y condiciones": "Terms and conditions",
    "Al realizar la reserva, el cliente confirma que los datos proporcionados son correctos (fecha, hora, vuelo, hotel/dirección y número de pasajeros).":
      "By making the booking, the customer confirms that the information provided is correct (date, time, flight, hotel/address and number of passengers).",
    "Los cambios o cancelaciones deberán comunicarse con antelación.":
      "Changes or cancellations must be communicated in advance.",
    "Todos los impuestos están incluidos en el precio mostrado. No se añadirán impuestos ni cargos adicionales durante el proceso de pago.":
      "All taxes are included in the price shown. No additional taxes or charges will be added during the payment process.",
    "El cliente deberá estar en el punto de recogida a la hora indicada y llevar consigo sus pertenencias. La empresa no se responsabiliza de objetos olvidados.":
      "The customer must be at the pickup point at the indicated time and carry their belongings with them. The company is not responsible for forgotten items.",
    "Al confirmar la reserva, el cliente acepta estos términos y condiciones.":
      "By confirming the booking, the customer accepts these terms and conditions.",

    // Step 4 — pending payment
    "Reserva pendiente de pago": "Booking pending payment",
    "Tu reserva está archivada. Para asignarte conductor, completa el pago.":
      "Your booking is on hold. To assign a driver, complete the payment.",
    "Número de reserva": "Booking number",
    "Pagar ahora": "Pay now",
    "También puedes pagar más tarde desde": "You can also pay later from",
    "\u201CConsultar mi reserva\u201D": "\u201CBooking lookup\u201D",
    "con tu teléfono.": "using your phone number.",

    // Client lookup
    "Consulta tu servicio": "Look up your service",
    "¿Quién te recoge?": "Who's picking you up?",
    "Ej. RSV-1001": "e.g. RSV-1001",
    "Teléfono": "Phone",
    "Ej. 611 22 33 44": "e.g. 611 22 33 44",
    "Buscar mi reserva": "Find my booking",
    "Importe del servicio": "Service amount",
    "Pagado": "Paid",
    "Pendiente de pago": "Pending payment",
    "Reservado vía": "Booked via",
    "Reserva": "Booking",
    "Estado": "Status",
    "Cliente": "Customer",
    "Fecha / Hora": "Date / Time",
    "Recogida": "Pickup",
    "Destino": "Destination",
    "pasajero(s)": "passenger(s)",
    "Vuelo ": "Flight ",
    "Tu conductor": "Your driver",
    "Aún no hemos asignado conductor. Te lo notificaremos en breve.":
      "We haven't assigned a driver yet. We'll notify you shortly.",
    "Atención al cliente": "Customer support",
    "¿Necesitas algo?": "Need anything?",
    "Solicita una": "Request an",
    "factura": "invoice",
    ", un": ", a",
    "cambio de hora": "time change",
    "o cuéntanos cualquier otra consulta.": "or tell us anything else.",
    "Motivo": "Reason",
    "Solicitar factura": "Request invoice",
    "Cambio de hora": "Time change",
    "Otra consulta": "Other query",
    "Mensaje": "Message",
    "Cuéntanos con detalle qué necesitas…": "Tell us in detail what you need…",
    "Enviar": "Send",
    "Mensaje enviado": "Message sent",
    "Nuestro centro de control se pondrá en contacto contigo en breve.":
      "Our control center will contact you shortly.",
    "Enviar otro mensaje": "Send another message",
    "Por favor escribe un mensaje.": "Please write a message.",
    "Pago cancelado. Puedes intentarlo otra vez.": "Payment cancelled. You can try again.",
    "Verificando pago…": "Verifying payment…",
    "¡Pago recibido! Gracias.": "Payment received! Thank you.",
    "La sesión de pago ha expirado.": "The payment session has expired.",
    "Estamos confirmando tu pago. Refresca la página en unos minutos para ver el estado actualizado.":
      "We're confirming your payment. Refresh in a few minutes to see the updated status.",

    // AI chat widget
    "Abrir chat de ayuda": "Open help chat",
    "Asistente DS Travel Tours": "DS Travel Tours assistant",
    "Nueva conversación": "New conversation",
    "Nueva": "New",
    "Cerrar chat": "Close chat",
    "¿Consulta sobre tu reserva?": "Question about your booking?",
    "Verifícate primero →": "Verify first →",
    "Verificación (opcional)": "Verification (optional)",
    "Nº de reserva (DSG-XXXXX)": "Booking number (DSG-XXXXX)",
    "Email o teléfono": "Email or phone",
    "Cerrar": "Close",
    "Escribe tu mensaje…": "Type your message…",
    "Escribiendo…": "Typing…",
    "Escalado a supervisor": "Escalated to supervisor",
    "¡Hola! Soy SofIA, la asistente virtual de DS Travel Tours. ¿En qué puedo ayudarte?":
      "Hi! I'm SofIA, the virtual assistant of DS Travel Tours. How can I help you?",

    // Language switcher
    "Idioma": "Language",
  },
};

const LangCtx = createContext({ lang: "es", setLang: () => {}, t: (s) => s });

export function LangProvider({ children }) {
  const [lang, setLang] = useState("es");
  const t = useCallback(
    (key) => {
      if (lang === "es") return key;
      return DICT.en[key] ?? key;
    },
    [lang]
  );
  return <LangCtx.Provider value={{ lang, setLang, t }}>{children}</LangCtx.Provider>;
}

export function useLang() {
  return useContext(LangCtx);
}
